import React, { Component } from "react";

import PropTypes from "prop-types";

import RequiredMark from "components/RequiredMark";
import Suggestion from "components/StoreLocator/Strategy/TypeAhead/Suggestion";

import "./TypeAhead.css";

const debounce = (func, delay, immediate) => {
  let timeout;

  return (...args) => {
    const context = this;

    const later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, delay);

    if (immediate && !timeout) func.apply(context, args);
  };
};

class TypeAhead extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isDropdownVisible: false,
      inputValue: "",
    };

    this.typeAheadRef = React.createRef();
    this.fetchPredictions = this.fetchPredictions.bind(this);
    this.getLimitedSuggestions = this.getLimitedSuggestions.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.debounceFetchPredictions = debounce(
      this.fetchPredictions.bind(this),
      props.debounce
    );
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  handleClickOutside(event) {
    if (
      this.typeAheadRef &&
      !this.typeAheadRef.current.contains(event.target)
    ) {
      this.setState({ isDropdownVisible: false });
    }
  }

  handleInputFocus() {
    const { suggestions } = this.props;
    if (suggestions && suggestions.length > 0) {
      this.setState({ isDropdownVisible: true });
    }
  }

  fetchPredictions() {
    const { input, clearSuggestions, fetchPredictions } = this.props;

    if (input.length) {
      fetchPredictions();
      this.setState({ isDropdownVisible: true });
    } else {
      clearSuggestions();
      this.setState({ isDropdownVisible: false });
    }
  }

  handleChange({ target: { value } }) {
    const { name, onChange, clearSuggestions } = this.props;
    onChange({ [name]: value });
    this.setState({ inputValue: value });

    if (!value) {
      clearSuggestions();
      this.setState({ isDropdownVisible: false });
    }

    this.debounceFetchPredictions();
  }

  handleSelect(suggestion) {
    const { clearSuggestions, handleSelect } = this.props;
    clearSuggestions();
    this.setState({ isDropdownVisible: false });
    handleSelect(suggestion);
  }

  getLimitedSuggestions() {
    const { inputValue } = this.state;
    const {
      suggestions = [],
      limitResults,
      removeDuplicatePredictions,
    } = this.props;

    const input = (inputValue || "").trim();
    if (!input) return [];

    let filtered = suggestions.filter(
      ({ description }) =>
        description && description.toLowerCase().includes(input.toLowerCase())
    );

    if (removeDuplicatePredictions) {
      const seen = new Set();
      filtered = filtered.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
    }

    if (limitResults > 0) {
      return filtered.slice(0, limitResults);
    }
    return filtered;
  }

  render() {
    const {
      className,
      disabled,
      input,
      label,
      name,
      quidBase,
      suggestions = [],
      required,
    } = this.props;

    const { isDropdownVisible } = this.state;
    const showSuggestions = isDropdownVisible && suggestions.length > 0;

    return (
      <div
        ref={this.typeAheadRef}
        className={`type-ahead form__control-group ${className}`}
        data-quid={quidBase}
        key={name}
      >
        <label
          className="text-field__label"
          data-quid={`${quidBase}-label`}
          htmlFor={name}
        >
          {required ? <RequiredMark /> : null} {label}
        </label>

        <input
          data-quid={`${quidBase}-input`}
          onChange={this.handleChange}
          onFocus={this.handleInputFocus}
          onClick={this.handleInputFocus}
          placeholder={label}
          name={name}
          type="text"
          value={input}
          disabled={disabled}
          required={required}
        />

        <div className="type-ahead__wrapper">
          <div
            className="type-ahead__options"
            data-quid={`${quidBase}-results`}
            style={{ display: showSuggestions ? "block" : "none" }}
          >
            {showSuggestions &&
              this.getLimitedSuggestions().map((suggestion, index) => {
                const { description, placeId } = suggestion;
                return (
                  <Suggestion
                    suggestion={suggestion}
                    input={input}
                    key={placeId || description}
                    dataQuid={`${quidBase}-results-${index + 1}`}
                    handleSelect={this.handleSelect}
                  />
                );
              })}
          </div>
        </div>
      </div>
    );
  }
}

TypeAhead.propTypes = {
  className: PropTypes.string,
  clearSuggestions: PropTypes.func.isRequired,
  debounce: PropTypes.number,
  disabled: PropTypes.bool,
  input: PropTypes.string,
  label: PropTypes.string,
  limitResults: PropTypes.number,
  name: PropTypes.string,
  quidBase: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  fetchPredictions: PropTypes.func.isRequired,
  handleSelect: PropTypes.func.isRequired,
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({ description: PropTypes.string.isRequired })
  ).isRequired,
  removeDuplicatePredictions: PropTypes.bool,
  required: PropTypes.bool,
};

TypeAhead.defaultProps = {
  className: "",
  debounce: 500,
  disabled: false,
  input: "",
  label: "",
  limitResults: 0,
  name: "",
  options: {},
  removeDuplicatePredictions: false,
  required: false,
};

export default TypeAhead;
